<template>
  <header class="sticky-header">
    <EnvIndicator />
    <div class="header-container">
      <div class="header-content d-flex align-items-center position-relative">
        <nav class="navigation">
          <div class="d-flex gap-5">
            <router-link
              :to="{ name: 'JobList' }"
              class="d-flex flex-grow-1"
            >
              <img
                class="logo"
                :src="logo"
                alt="Sealed"
              />
            </router-link>
            <div class="desktop-search-button">
              <Button
                v-if="currentAuthenticatedUser"
                type="button"
                variant="secondary-on-background"
                size="sm"
                :text="t('common_actions.search')"
                icon="search"
                @click="showOmniSearch = true"
              />
            </div>
          </div>

          <div
            v-if="currentAuthenticatedUser"
            class="right-nav"
          >
            <template
              v-for="item in navItems"
              :key="`${item.label}-menu-button`"
            >
              <Button
                v-if="item.onClick && item.label"
                type="button"
                :text="item.label"
                :icon="item.icon"
                @click="item.onClick"
                size="sm"
                variant="secondary-on-background"
              />
            </template>
            <DropdownMenu
              v-if="dropDownNavItems.menuItems"
              :menuItems="dropDownNavItems.menuItems"
              :icon="dropDownNavItems.icon"
            />
          </div>
          <div
            v-if="currentAuthenticatedUser"
            class="menu-container"
          >
            <IconButton
              type="button"
              variant="secondary-on-background"
              icon="search"
              @click="showOmniSearch = true"
            />
            <DropdownMenu
              icon="menu"
              :menuItems="combinedNavItems"
            />
          </div>
        </nav>
      </div>
    </div>
    <Teleport
      to="#modal-target"
      v-if="showOmniSearch && !isContractorView()"
    >
      <OmniSearch @close="showOmniSearch = false" />
    </Teleport>
    <Teleport
      to="#modal-target"
      v-if="showOmniSearch && isContractorView()"
    >
      <OmniSearchContractor @close="showOmniSearch = false" />
    </Teleport>
  </header>
</template>

<script setup lang="ts">
import logo from '@/assets/images/sealed-logo.svg';
import OmniSearchContractor from '@/components/OmniSearchContractor.vue';
import Button from '@component-module/components/Button/Button.vue';
import DropdownMenu, {
  type MenuItem
} from '@component-module/components/DropdownMenu/DropdownMenu.vue';
import IconButton from '@component-module/components/IconButton/IconButton.vue';
import EnvIndicator from '@shared/components/EnvIndicator.vue';
import OmniSearch from '@shared/components/OmniSearch.vue';
import { useUserStore } from '@shared/stores/user';
import { storeToRefs } from 'pinia';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

const userStore = useUserStore();
const router = useRouter();
const { t } = useI18n();
const { currentAuthenticatedUser } = storeToRefs(userStore);
const showOmniSearch = ref(false);

const navItems: MenuItem[] = [
  {
    icon: 'view_list',
    label: t('jobs_list.nav_label'),
    onClick: () => router.push({ name: 'JobList' })
  },
  {
    icon: 'handyman',
    label: t('contractors_list.nav_label'),
    onClick: () => router.push({ name: 'ContractorsList' })
  },
  {
    icon: 'home_repair_service',
    label: t('common_equipment.equipment'),
    onClick: () => router.push({ name: 'Equipment' })
  }
];

const secondaryNavItems = [
  {
    icon: 'add',
    label: t('common_actions.add_new_contractor'),
    onClick: () => router.push({ name: 'ContractorCreate' })
  },
  {
    icon: 'settings',
    label: t('common_programs.programs'),
    onClick: () => router.push({ name: 'ProgramsList' })
  },
  {
    icon: 'assignment',
    label: t('terms_and_conditions.title'),
    onClick: () => router.push({ name: 'TermsAndConditionsList' })
  },
  {
    icon: 'electric_bolt',
    label: t('energy_providers.title'),
    onClick: () => router.push({ name: 'EnergyProvidersList' })
  },
  {
    icon: 'ad',
    label: t('announcements.title'),
    onClick: () => router.push({ name: 'AnnouncementsList' })
  },

  {
    icon: 'explore',
    label: t('common_programs.program_explorer.title'),
    onClick: () => router.push({ name: 'ProgramExplorer' })
  },
  {
    icon: 'logout',
    label: t('common_actions.sign_out'),
    onClick: () => router.push({ name: 'logout' })
  }
];

const dropDownNavItems: MenuItem = {
  icon: 'more_vert',
  label: t('common_labels.menu'),
  menuItems: secondaryNavItems
};

const combinedNavItems = [...navItems, ...secondaryNavItems];

const isContractorView = () => {
  // Use regex to match any route that starts with 'Contractor'
  // e.g. 'ContractorCreate', 'ContractorEdit', 'ContractorView'
  if (!router.currentRoute.value.name) return false;
  const contractorRouteMatch = RegExp('Contractor').test(
    router.currentRoute.value.name.toString()
  );
  if (contractorRouteMatch) {
    return true;
  } else return false;
};

window.addEventListener('keydown', (e) => {
  if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
    e.preventDefault();
    showOmniSearch.value = true;
  }
});
</script>

<style lang="scss" scoped>
.sticky-header {
  position: sticky;
  top: 0;
  z-index: 10;
  margin-bottom: 1.5rem;
}

.header-container {
  padding: 1.5rem 0;
  @media screen and (min-width: 992px) {
    padding: 2rem 0;
  }
}

.header-content {
  padding: 0 1.75rem;
}

.logo {
  height: 20px;
  @media screen and (min-width: 992px) {
    width: 133px;
    height: 40px;
  }
}

.navigation {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;

  .right-nav,
  .desktop-search-button {
    display: none;
    @media screen and (min-width: 992px) {
      display: flex;
      align-items: center;
      gap: 1rem;
    }
  }
  .menu-container {
    display: flex;
    gap: 1rem;
    @media screen and (min-width: 992px) {
      display: none;
    }
  }
}
</style>
